import { Button, Form, Layout, Tooltip, message } from 'antd';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { MenuCustom } from 'app/components/commons/MenuCustom/MenuCustom';
import { Notification } from 'app/components/commons/Notification/Notification';
import { FormLegacyRenderProp } from 'app/components/forms/FormLegacyRenderProp';
import { ConfigContext } from 'app/context/ConfigContext/ConfigContext';
import {
  ViewEditModeContext,
  ViewEditModeContextProvider,
} from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useGetHotelInfo } from 'app/hooks/data/core/admin/hotels/useGetHotelInfo';
import {
  useGetPackageByHotelId,
  useUpdateHotel,
} from 'app/hooks/data/hotels/useHotel';
import { useCountries } from 'app/hooks/data/useCountries';
import { ChannelManager } from 'app/typings/channelManagers';
import { ApiError } from 'app/typings/error';
import type { Hotel } from 'app/typings/hotels/Hotel/Hotel';
import { RateMode } from 'app/typings/rateModes';
import type { HotelErrorCode } from 'app/utils/errors/HotelErrors';
import { retrieveHotelErrorMessage } from 'app/utils/errors/HotelErrors';

import { Contacts } from '../HotelDetail/Contacts/Contacts';
import { Copywriting } from '../HotelDetail/Copywriting/Copywriting';
import { CoverAndDiscover } from '../HotelDetail/CoverAndDiscover/CoverAndDiscover';
import { Finance } from '../HotelDetail/Finance/Finance';
import { General } from '../HotelDetail/General/General';
import { MarketingBrief } from '../HotelDetail/MarketingBrief/MarketingBrief';
import { Services } from '../HotelDetail/Services/Services';
import { Spaces } from '../HotelDetail/Spaces/Spaces';

import { DuplicateModal } from './DuplicateModal/DuplicateModal';
import './HotelDetail.scss';
import { MigrateModal } from './MigrateModal/MigrateModal';
import { PackagesRoomsAmenities } from './PackagesRoomsAmenities/PackagesRoomsAmenities';

enum MenuItemName {
  // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
  General = 'General',
  // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
  Services = 'Services',
  // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
  Spaces = 'Spaces',
  Brief = 'Brief',
  // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
  Finance = 'Finance',
  // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
  Copywriting = 'Copywriting',
  Cover = 'Cover & Discover',
  // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
  PackagesRoomsAmenities = 'Packages, Rooms & Amenities',
  // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
  Contacts = 'Contacts',
}

type MenuItem = {
  name: MenuItemName;
};

const MENU_ITEMS = [
  { name: MenuItemName.PackagesRoomsAmenities },
  { name: MenuItemName.General },
  { name: MenuItemName.Contacts },
  { name: MenuItemName.Finance },
  { name: MenuItemName.Services },
  { name: MenuItemName.Spaces },
  { name: MenuItemName.Brief },
  { name: MenuItemName.Copywriting },
  { name: MenuItemName.Cover },
];

const STANDALONE_MENUS = new Set([
  MenuItemName.General,
  MenuItemName.Finance,
  MenuItemName.Copywriting,
  MenuItemName.PackagesRoomsAmenities,
  MenuItemName.Contacts,
]);

const HotelEditContent = () => {
  const { id: hotelId } = useParams<{ id: string }>();
  const { data: packagesList } = useGetPackageByHotelId(Number(hotelId));

  const history = useHistory();
  const location = useLocation();
  const { data: hotel, refetch: reloadHotel } = useGetHotelInfo(+hotelId);
  const { mutateAsync: updateHotel, error: editHotelError } = useUpdateHotel(
    Number(hotelId)
  );

  const config = useContext(ConfigContext);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [migrateModalOpen, setMigrateModalOpen] = useState(false);
  const { data: countries } = useCountries();

  const { enterViewMode } = useContext(ViewEditModeContext);

  useEffect(() => {
    if (document.readyState === 'complete') {
      const query = new URLSearchParams(location.search);
      const successfullyMerge = query.get('successfullyMerge');

      if (successfullyMerge) {
        message.success('Your hotels will be successfully merged very soon');
        query.delete('successfullyMerge');
        history.replace({ search: query.toString() });
      }
    }
  }, [history, location.search]);

  const isInstanceOfApiError = editHotelError instanceof ApiError;

  const hotelError =
    isInstanceOfApiError && editHotelError.jsonObject
      ? retrieveHotelErrorMessage(
          editHotelError.jsonObject.errorCode as HotelErrorCode
        )
      : { errorTitle: '', errorMessage: '' };

  const [form] = Form.useForm();
  const [menu, setMenu] = useState<MenuItem>(MENU_ITEMS[0]);

  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');

    if (!tab) {
      return;
    }

    const target = MENU_ITEMS.find(
      (item) => item.name.toLowerCase() === tab.toLowerCase()
    );

    if (target) {
      setMenu(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Need to run only once on mount, not when location.search changes
  }, []);

  useEffect(() => {
    form.resetFields();
  }, [form, hotel]);

  const renderMenu = () => (
    <MenuCustom
      menuItems={MENU_ITEMS}
      selectedMenu={menu}
      setSelectedMenu={(menuItem: MenuItem) => {
        setMenu(menuItem);
        enterViewMode();
      }}
    />
  );

  const dispatchMenuSection = () => {
    switch (menu.name) {
      case MenuItemName.General:
        return (
          <General
            hotelId={Number(hotelId)}
            headerMenu={renderMenu()}
            headerExtra={[...headerExtra]}
          />
        );

      case MenuItemName.Contacts:
        return (
          <Contacts
            hotelId={Number(hotelId)}
            headerMenu={renderMenu()}
            headerExtra={[...headerExtra]}
          />
        );

      case MenuItemName.Finance:
        return (
          <Finance
            hotelId={Number(hotelId)}
            headerMenu={renderMenu()}
            headerExtra={[...headerExtra]}
          />
        );
      case MenuItemName.Copywriting:
        return (
          <Copywriting
            hotelId={Number(hotelId)}
            headerMenu={renderMenu()}
            headerExtra={[...headerExtra]}
          />
        );
      case MenuItemName.Services:
        return (
          <Services
            hotel={hotel}
            headerMenu={renderMenu()}
            headerExtra={[...headerExtra]}
            reloadHotel={reloadHotel}
          />
        );
      case MenuItemName.Spaces:
        return (
          <Spaces
            hotel={hotel}
            headerMenu={renderMenu()}
            headerExtra={[...headerExtra]}
            reloadHotel={reloadHotel}
          />
        );
      case MenuItemName.Brief:
        return (
          <MarketingBrief
            hotel={hotel}
            headerMenu={renderMenu()}
            headerExtra={[...headerExtra]}
            reloadHotel={reloadHotel}
          />
        );
      case MenuItemName.Cover:
        if (!hotel) {
          return null;
        }

        return (
          <CoverAndDiscover
            hotelId={hotel.id}
            headerMenu={renderMenu()}
            headerExtra={[...headerExtra]}
          />
        );

      case MenuItemName.PackagesRoomsAmenities:
        return (
          <PackagesRoomsAmenities
            hotelId={Number(hotelId)}
            headerMenu={renderMenu()}
            headerExtra={[...headerExtra]}
          />
        );
      default:
        return null;
    }
  };

  const handleSubmit = useCallback(
    async (values: Hotel) => {
      const emails = values.notifEmails.map(({ email }) => email);
      const hasDuplicateEmails = new Set(emails).size !== emails.length;

      if (hasDuplicateEmails) {
        message.error('Duplicate emails not allowed');

        return;
      }

      await updateHotel({
        ...hotel,
        ...values,
        location: {
          ...hotel?.location,
          ...values.location,
        },
        address: {
          ...hotel?.address,
          ...values.address,
          subCity:
            values.address?.subCity?.trim() === ''
              ? undefined
              : values.address?.subCity?.trim(),
        },
        notifEmails: values.notifEmails,
        channelManager:
          values.channelManager === ChannelManager.NONE
            ? undefined
            : values.channelManager,
        channelManagerCredentials:
          values.channelManager === ChannelManager.MEWS
            ? values.channelManagerCredentials
            : undefined,
        rateMode:
          values.channelManager === undefined ||
          values.channelManager === ChannelManager.NONE
            ? undefined
            : values.rateMode || RateMode.MULTI_RATE,
      });

      await reloadHotel();
      enterViewMode();
    },
    [updateHotel, hotel, reloadHotel, enterViewMode]
  );

  if (!hotel || hotelId !== `${hotel.id}`) {
    return null;
  }

  const renderMergeButton = (hId: string) => (
    <div key={'merge-button'} className="merge-btn">
      <Button
        size="large"
        onClick={() => {
          window.open(`/hotel-migration/${hId}`, '_blank');
        }}
      >
        Merge
      </Button>
    </div>
  );

  const renderDuplicateButton = () => {
    if (hotel.isLegacy === false) {
      return null;
    }

    return (
      <div key={'duplicate-button'} className="duplicate-btn">
        <Button size="large" onClick={() => setDuplicateModalOpen(true)}>
          Duplicate
        </Button>
      </div>
    );
  };

  const renderPreviewButton = () => {
    if (!hotel || !hotel.slug) {
      return null;
    }

    const locale =
      countries?.find((country) => country.id === hotel.countryId)?.locale ??
      'fr';

    return (
      <div key={'preview-button'} className="preview-btn">
        <Button
          size="large"
          onClick={() =>
            window.open(
              `${config?.appUrl}/${locale}/hotels/${hotel.slug}`,
              '_blank'
            )
          }
        >
          Preview
        </Button>
      </div>
    );
  };

  const renderMigrateButton = () => {
    if (hotel.isLegacy === false || !hotel || !hotel.slug) {
      return null;
    }

    const isParentHotel = hotel.id === hotel.parentHotelId;
    const isCmHotel =
      hotel.stockScrapperEnabled === false &&
      hotel.channelManager !== undefined &&
      hotel.channelManager !== ChannelManager.NONE;
    const isAutoInventoryHotel = hotel.stockScrapperEnabled === true;
    const isManualHotel =
      hotel.stockScrapperEnabled === false &&
      (hotel.channelManager === undefined ||
        hotel.channelManager === ChannelManager.NONE);
    const isSingleRate = isCmHotel && hotel.rateMode === RateMode.SINGLE_RATE;
    const hasEligibleRateMode =
      isAutoInventoryHotel || isManualHotel || isSingleRate;

    const disabledReason = () => {
      if (!isParentHotel) {
        return 'Only parent hotels can be migrated.';
      }

      if (!hasEligibleRateMode) {
        return 'Only single-rate hotels can be migrated.';
      }

      return undefined;
    };

    const canMigrate = isParentHotel && hasEligibleRateMode;

    return (
      <div key="migrate-button" className="preview-btn">
        <Tooltip title={disabledReason()}>
          <Button
            disabled={!canMigrate}
            size="large"
            onClick={() => setMigrateModalOpen(true)}
          >
            Migrate
          </Button>
        </Tooltip>
      </div>
    );
  };

  const shouldShowMergeButton =
    (hotel.id === hotel.parentHotelId ||
      hotel.stockScrapperEnabled ||
      (hotel.channelManager && hotel.channelManager !== ChannelManager.NONE)) &&
    packagesList?.packages.some((pkg) => !pkg.dayPackage) &&
    hotel.isLegacy;

  const headerExtra = [
    renderMigrateButton(),
    renderPreviewButton(),
    shouldShowMergeButton ? renderMergeButton(hotelId) : null,
    renderDuplicateButton(),
  ];

  return (
    <Layout className="hotel-detail">
      {STANDALONE_MENUS.has(menu.name) ? (
        dispatchMenuSection()
      ) : (
        <FormLegacyRenderProp
          form={form}
          onReset={() => form.resetFields()}
          onFinish={handleSubmit}
          initialValues={hotel}
        >
          {() => dispatchMenuSection()}
        </FormLegacyRenderProp>
      )}

      <Notification
        title={hotelError.errorTitle}
        content={hotelError.errorMessage}
        visible={isInstanceOfApiError}
        type="error"
        hasCancelButton={false}
        centered
      />

      <DuplicateModal
        open={duplicateModalOpen}
        onClose={() => setDuplicateModalOpen(false)}
        hotelId={hotelId}
      />

      <MigrateModal
        open={migrateModalOpen}
        onClose={() => setMigrateModalOpen(false)}
        hotel={hotel}
      />
    </Layout>
  );
};

export const HotelEdit = () => {
  return (
    <ViewEditModeContextProvider>
      <HotelEditContent />
    </ViewEditModeContextProvider>
  );
};
